import { NgClass } from '@angular/common';
import { ChangeDetectionStrategy, Component, input, output } from '@angular/core';

import { TranslationModule } from '@core/translation/translation.module';

/**
 * Segmented "pill" tab switcher: a contained row of buttons where the active
 * one renders as a raised white pill on a tinted track. Used to switch between
 * sibling content panels. Plain `<button>` elements + Tailwind — no PrimeNG,
 * no SCSS.
 *
 * The track background, padding and rounding are baked into the host, so
 * consumers just drop `<u2b-tab-switcher>` with no wrapper styling. On narrow
 * screens the track scrolls horizontally.
 *
 * Distinct from `u2b-segmented-toggle` (a form-control-style value picker with
 * an orange-filled active state): this one carries navigation semantics
 * (`role="tablist"`, `activeId`/`tabSelected`) and optional count badges.
 *
 * Pass Tolgee keys for labels; the component runs the translate pipe itself.
 */
export interface U2bTabSwitcherItem<T = string> {
    /** Value emitted when the tab is selected. Used as track key. */
    id: T;
    /** Tolgee key for the tab label. */
    labelKey: string;
    /** Optional Material icon name shown left of the label. */
    icon?: string;
    /** Optional count badge (hidden when undefined or 0). */
    count?: number;
}

@Component({
    selector: 'u2b-tab-switcher',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [NgClass, TranslationModule],
    host: {
        role: 'tablist',
        '[attr.aria-label]': 'ariaLabel() || null',
        class: 'inline-flex items-center gap-1 p-1 rounded-p-sm bg-p-surface-100 max-w-full overflow-x-auto',
    },
    template: `
        @for (tab of tabs(); track tab.id) {
            @let active = tab.id === activeId();
            <button
                type="button"
                role="tab"
                [attr.aria-selected]="active"
                (click)="tabSelected.emit(tab.id)"
                class="inline-flex items-center gap-2 px-3 py-1.5 rounded-p-sm border-0 text-sm font-medium cursor-pointer whitespace-nowrap transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-p-primary"
                [ngClass]="active
                    ? 'bg-white text-p-primary shadow-sm'
                    : 'bg-transparent text-p-text-muted hover:text-p-text'">
                @if (tab.icon) {
                    <span class="material-icons text-u2b-base" aria-hidden="true">{{ tab.icon }}</span>
                }
                <span>{{ tab.labelKey | translate }}</span>
                @if (tab.count) {
                    <span
                        class="px-1.5 py-0.5 rounded-full text-xs"
                        [class.bg-p-surface-200]="!active"
                        [class.text-p-text-muted]="!active"
                        [class.text-p-primary]="active"
                        [style.background]="active ? 'color-mix(in srgb, var(--p-primary-color) 15%, transparent)' : null">
                        {{ tab.count }}
                    </span>
                }
            </button>
        }
    `,
})
export class U2bTabSwitcherComponent<T = string> {

    readonly tabs = input.required<readonly U2bTabSwitcherItem<T>[]>();

    readonly activeId = input.required<T>();

    readonly ariaLabel = input<string>('');

    readonly tabSelected = output<T>();
}
